/**
 * PII Redaction Utility
 * Remove/mascara dados sensíveis de logs conforme LGPD
 */

// Lista de campos sensíveis que devem ser completamente removidos
const SENSITIVE_FIELDS = [
  'password',
  'token',
  'api_key',
  'secret',
  'authorization',
  'cookie',
  'session',
  'access_token',
  'refresh_token',
  'credit_card',
  'cvv',
  'pin',
];

// Lista de headers sensíveis
const SENSITIVE_HEADERS = [
  'authorization',
  'cookie',
  'set-cookie',
  'x-api-key',
  'x-auth-token',
];

/**
 * Mascara email: john.doe@example.com -> j***@example.com
 */
export function maskEmail(email: string): string {
  if (!email || typeof email !== 'string') return email;
  
  const [username, domain] = email.split('@');
  if (!username || !domain) return '[REDACTED]';
  
  if (username.length <= 1) {
    return `***@${domain}`;
  }
  
  return `${username[0]}***@${domain}`;
}

/**
 * Mascara telefone: +5511999998888 -> +55 ** *****-8888
 */
export function maskPhone(phone: string): string {
  if (!phone || typeof phone !== 'string') return phone;
  
  const digits = phone.replace(/\D/g, '');
  if (digits.length < 8) return '[REDACTED]';
  
  const lastFour = digits.slice(-4);
  return `+${digits.substring(0, 2)} ** *****-${lastFour}`;
}

/**
 * Mascara CPF: 123.456.789-00 -> ***.456.***-**
 */
export function maskCPF(cpf: string): string {
  if (!cpf || typeof cpf !== 'string') return cpf;
  
  const digits = cpf.replace(/\D/g, '');
  
  if (digits.length === 11) {
    // Format: ***.456.***-**
    return `***.${digits.substring(3, 6)}.***-**`;
  }
  
  return '[REDACTED]';
}

/**
 * Mascara IDs sensíveis: mostra primeiros 4 e últimos 4 caracteres
 */
export function maskId(id: string): string {
  if (!id || typeof id !== 'string' || id.length < 12) return '[REDACTED]';
  
  return `${id.substring(0, 4)}****${id.substring(id.length - 4)}`;
}

/**
 * Arredonda coordenadas GPS para ~110m de precisão (3 casas decimais)
 */
export function roundGPS(coords: { lat: number; lng: number }): { lat: number; lng: number } {
  return {
    lat: Math.round(coords.lat * 1000) / 1000,
    lng: Math.round(coords.lng * 1000) / 1000,
  };
}

export function roundCoordinates(lat: number, lng: number): { lat: number; lng: number } {
  return roundGPS({ lat, lng });
}

/**
 * Remove headers sensíveis de um objeto de headers
 */
export function redactHeaders(headers: Record<string, any>): Record<string, any> {
  const redacted: Record<string, any> = {};
  
  for (const [key, value] of Object.entries(headers)) {
    const lowerKey = key.toLowerCase();
    
    if (SENSITIVE_HEADERS.includes(lowerKey)) {
      redacted[key] = '[REDACTED]';
    } else {
      redacted[key] = value;
    }
  }
  
  return redacted;
}

/**
 * Redact recursivo de objeto
 */
function redactObject(obj: any, depth = 0): any {
  // Prevenir recursão infinita
  if (depth > 10) return '[MAX_DEPTH]';
  
  if (obj === null || obj === undefined) return obj;
  
  // Arrays
  if (Array.isArray(obj)) {
    return obj.map(item => redactObject(item, depth + 1));
  }
  
  // Objetos
  if (typeof obj === 'object') {
    const redacted: Record<string, any> = {};
    
    for (const [key, value] of Object.entries(obj)) {
      const lowerKey = key.toLowerCase();
      
      // Remover campos sensíveis completamente
      if (SENSITIVE_FIELDS.some(field => lowerKey.includes(field))) {
        redacted[key] = '[REDACTED]';
        continue;
      }
      
      // Mascarar email
      if (lowerKey.includes('email') && typeof value === 'string' && value.includes('@')) {
        redacted[key] = maskEmail(value);
        continue;
      }
      
      // Mascarar telefone
      if ((lowerKey.includes('phone') || lowerKey.includes('telefone')) && typeof value === 'string') {
        redacted[key] = maskPhone(value);
        continue;
      }
      
      // Mascarar CPF
      if (lowerKey.includes('cpf') && typeof value === 'string') {
        redacted[key] = maskCPF(value);
        continue;
      }
      
      // Mascarar IDs sensíveis (user_id, session_id já são ok)
      if (lowerKey.includes('card') && typeof value === 'string') {
        redacted[key] = maskId(value);
        continue;
      }
      
      // Arredondar coordenadas
      if (key === 'latitude' || key === 'lat') {
        redacted[key] = typeof value === 'number' ? Math.round(value * 1000) / 1000 : value;
        continue;
      }
      
      if (key === 'longitude' || key === 'lng') {
        redacted[key] = typeof value === 'number' ? Math.round(value * 1000) / 1000 : value;
        continue;
      }
      
      // Headers especiais
      if (lowerKey === 'headers') {
        redacted[key] = redactHeaders(value);
        continue;
      }
      
      // Recursão para objetos aninhados
      if (typeof value === 'object') {
        redacted[key] = redactObject(value, depth + 1);
      } else {
        redacted[key] = value;
      }
    }
    
    return redacted;
  }
  
  return obj;
}

/**
 * Função principal: redact PII de qualquer objeto
 */
export function redactPII(data: any): any {
  try {
    return redactObject(data);
  } catch (error) {
    console.error('Error in redactPII:', error);
    return { error: 'Failed to redact PII' };
  }
}

/**
 * Sanitiza URL removendo query params sensíveis
 */
export function sanitizeUrl(url: string): string {
  try {
    const urlObj = new URL(url);
    const sensitiveParams = ['token', 'password', 'api_key', 'secret', 'access_token', 'key'];
    
    sensitiveParams.forEach(param => {
      if (urlObj.searchParams.has(param)) {
        urlObj.searchParams.set(param, '[REDACTED]');
      }
    });
    
    return decodeURIComponent(urlObj.toString());
  } catch {
    // Se não é URL válida, retornar como está
    return url;
  }
}
